import axios from '../config/axios';

const API_BASE = '/external-shipment-commissions';

const externalCommissionService = {
  getAll: (page = 1, perPage = 20, filters = {}) =>
    axios.get(API_BASE, { params: { page, per_page: perPage, ...filters } }),

  getById: (id) => axios.get(`${API_BASE}/${id}`),

  update: (id, data) => axios.put(`${API_BASE}/${id}`, data),

  create: (data) => axios.post(API_BASE, data),

  delete: (id) => axios.delete(`${API_BASE}/${id}`),

  calculatePreview: (mlSheetId, vehicleOwnerId, linkedCommissionId) =>
    axios.get(`${API_BASE}/calculate-preview`, {
      params: {
        ml_sheet_id: mlSheetId,
        vehicle_owner_id: vehicleOwnerId,
        linked_commission_id: linkedCommissionId,
      },
    }),

  addPayment: (id, data) => {
    if (data instanceof FormData) {
      return axios.post(`${API_BASE}/${id}/payments`, data, {
        headers: { 'Content-Type': undefined },
      });
    }
    return axios.post(`${API_BASE}/${id}/payments`, data);
  },

  getPayment: (paymentId) => axios.get(`${API_BASE}/payments/${paymentId}`),

  updatePayment: (paymentId, data) => {
    if (data instanceof FormData) {
      return axios.put(`${API_BASE}/payments/${paymentId}`, data, {
        headers: { 'Content-Type': undefined },
      });
    }
    return axios.put(`${API_BASE}/payments/${paymentId}`, data);
  },

  getPaymentReceipt: (paymentId) => axios.get(`${API_BASE}/payments/${paymentId}/receipt`),

  deletePayment: (paymentId) => axios.delete(`${API_BASE}/payments/${paymentId}`),

  refreshSettlement: (id) => axios.post(`${API_BASE}/${id}/refresh-settlement`),

  bulkPayment: (data) => axios.post(`${API_BASE}/bulk-payment`, data),

  getBulkSettlementAgents: () => axios.get(`${API_BASE}/bulk-settlement/agents`),

  /** @deprecated use getBulkSettlementOwners with agent filter */
  getBulkSettlementOwners: (agentId) =>
    axios.get(`${API_BASE}/bulk-settlement/owners`, {
      params: agentId ? { agent_id: agentId } : {},
    }),

  /** Vehicle owners for bulk settlement (optionally scoped to a booking agent). */
  loadBulkSettlementOwners: async (agentId = '') => {
    try {
      const res = await axios.get(`${API_BASE}/bulk-settlement/owners`, {
        params: agentId ? { agent_id: agentId } : {},
      });
      const rows = res.data?.data || [];
      return rows.map((item) => ({
        vehicle_owner_id: item.vehicle_owner_id,
        vehicle_owner_name: item.vehicle_owner_name || item.owner_name || item.vehicle_owner_id,
      }));
    } catch (_) {
      const res = await axios.get('/vehicle-owners', { params: { all: true } });
      const rows = res.data?.data || res.data || [];
      return (Array.isArray(rows) ? rows : []).map((owner) => ({
        vehicle_owner_id: owner.vehicle_owner_id,
        vehicle_owner_name: owner.owner_name || owner.vehicle_owner_id,
      }));
    }
  },

  /** Load booking agents for bulk settlement — optionally scoped to a vehicle owner. */
  loadBulkSettlementAgents: async (vehicleOwnerId = '') => {
    const merged = new Map();

    const addAgent = (agentId, agentName) => {
      if (!agentId) return;
      merged.set(agentId, {
        agent_id: agentId,
        agent_name: agentName || agentId,
      });
    };

    const addRows = (rows) => {
      (rows || []).forEach((item) => {
        if (item.agent_id) {
          addAgent(item.agent_id, item.agent_name || item.name || item.username);
        }
      });
    };

    const addFromCommissions = (commissions) => {
      (commissions || []).forEach((record) => {
        const agents = record?.booked_agent_commission_json?.agents || [];
        agents.forEach((agent) => {
          addAgent(agent.agent_id, agent.agent_name);
        });
      });
    };

    // 1) Dedicated bulk endpoints (when backend is up to date)
    try {
      const res = await axios.get(`${API_BASE}/bulk-settlement/agents`, {
        params: vehicleOwnerId ? { vehicle_owner_id: vehicleOwnerId } : {},
      });
      addRows(res.data?.data);
    } catch (_) {
      // route missing — continue to fallbacks
    }

    // 2) Commission list — works on existing API, includes booking agents on each sheet
    try {
      const res = await axios.get(API_BASE, { params: { per_page: 100, page: 1 } });
      addFromCommissions(res.data?.data);
    } catch (_) {
      // ignore
    }

    // 3) Company users with Agent role
    try {
      const usersRes = await axios.get('/users', { params: { all: true } });
      const users = usersRes.data?.data || usersRes.data || [];
      users
        .filter((u) => (u.role_name || u.role) === 'Agent')
        .forEach((u) => addAgent(u.user_id, u.name || u.username));
    } catch (_) {
      // ignore
    }

    return Array.from(merged.values()).sort((a, b) =>
      (a.agent_name || a.agent_id).localeCompare(b.agent_name || b.agent_id, undefined, { sensitivity: 'base' })
    );
  },

  getBulkSettlementRecords: (agentId, vehicleOwnerId, params = {}) =>
    axios.get(`${API_BASE}/bulk-settlement/records`, {
      params: {
        agent_id: agentId,
        vehicle_owner_id: vehicleOwnerId,
        ...params,
      },
    }),

  buildAgentSettlementRowFromCommission: (record, agent) => {
    const paid = parseFloat(agent.paid_freight || 0);
    const toPay = parseFloat(agent.to_pay_freight || 0);
    let freight_type = 'UNKNOWN';
    if (toPay > 0 && paid <= 0) freight_type = 'TO_PAY';
    else if (paid > 0 && toPay <= 0) freight_type = 'PAID_ORIGIN';
    else if (paid > 0 && toPay > 0) freight_type = 'MIXED';

    const payOwner = Math.round(parseFloat(agent.remaining_pay_to_owner || 0));
    const receiveOwner = Math.round(parseFloat(agent.remaining_receive_from_owner || 0));
    const net = payOwner - receiveOwner;
    const netPay = Math.max(net, 0);
    const netCollect = Math.max(-net, 0);
    let netDirection = 'SETTLED';
    let netLabel = 'Settled';
    if (netPay > 0) {
      netDirection = 'PAY_TO_OWNER';
      netLabel = `Pay ₹${netPay} to vehicle owner`;
    } else if (netCollect > 0) {
      netDirection = 'RECEIVE_FROM_OWNER';
      netLabel = `Get ₹${netCollect} commission from vehicle owner`;
    }

    return {
      external_commission_id: record.external_commission_id,
      ml_sheet_id: record.ml_sheet_id,
      vehicle_owner_id: record.vehicle_owner_id,
      vehicle_owner_name: record.vehicle_owner_name,
      agent_id: agent.agent_id,
      agent_name: agent.agent_name,
      freight_type,
      total_freight: agent.total_freight,
      paid_freight: agent.paid_freight,
      balance_freight: agent.to_pay_freight,
      total_agent_commission: agent.agent_commission_amount,
      agent_commission_retained: agent.commission_retained_from_cash,
      pay_to_owner_due: agent.pay_to_owner_due,
      receive_from_owner_due: agent.receive_from_owner_due,
      total_pay_to_owner_due: agent.pay_to_owner_due,
      total_receive_from_owner_due: agent.receive_from_owner_due,
      remaining_pay_to_owner: netPay,
      remaining_receive_from_owner: netCollect,
      remaining_remit_from_agent: 0,
      remaining_net: netCollect,
      remaining_settlement_total: netPay || netCollect,
      net_settlement_amount: netPay || netCollect,
      net_settlement_direction: netDirection,
      net_settlement_label: netLabel,
      action_pay_to_owner: netPay,
      action_get_from_owner: netCollect,
      commission_to_receive: receiveOwner,
      owner_payment_due: payOwner,
      amount_paid_to_owner: agent.amount_paid_to_owner,
      amount_received_from_owner: agent.amount_received_from_owner,
      settlement_summary: netLabel,
      payment_status: (netPay || netCollect) > 0
        ? (agent.payment_status || record.payment_status || 'PENDING')
        : 'PAID',
    };
  },

  sumBulkSettlementTotals: (records) => ({
    total_freight: Math.round(records.reduce((s, r) => s + parseFloat(r.total_freight || 0), 0)),
    paid_freight: Math.round(records.reduce((s, r) => s + parseFloat(r.paid_freight || 0), 0)),
    balance_freight: Math.round(records.reduce((s, r) => s + parseFloat(r.balance_freight || 0), 0)),
    total_agent_commission: Math.round(records.reduce((s, r) => s + parseFloat(r.total_agent_commission || 0), 0)),
    total_owner_share: Math.round(records.reduce((s, r) => s + parseFloat(r.owner_commission_amount || 0), 0)),
    remaining_pay_to_owner: Math.round(records.reduce((s, r) => s + parseFloat(r.remaining_pay_to_owner || 0), 0)),
    remaining_receive_from_owner: Math.round(records.reduce((s, r) => s + parseFloat(r.remaining_receive_from_owner || 0), 0)),
    remaining_net: Math.round(records.reduce((s, r) => s + parseFloat(r.remaining_net || 0), 0)),
    total_pay_to_owner_due: Math.round(records.reduce((s, r) => s + parseFloat(r.pay_to_owner_due || 0), 0)),
    total_receive_from_owner_due: Math.round(records.reduce((s, r) => s + parseFloat(r.receive_from_owner_due || 0), 0)),
  }),

  loadBulkSettlementRecords: async (agentId, vehicleOwnerId, params = {}) => {
    try {
      const res = await axios.get(`${API_BASE}/bulk-settlement/records`, {
        params: {
          agent_id: agentId,
          vehicle_owner_id: vehicleOwnerId,
          ...params,
        },
      });
      return {
        data: res.data?.data || [],
        totals: res.data?.totals || {},
      };
    } catch (_) {
      const res = await axios.get(API_BASE, { params: { per_page: 100, page: 1 } });
      const commissions = res.data?.data || [];
      const data = [];
      commissions.forEach((record) => {
        if (vehicleOwnerId && record.vehicle_owner_id !== vehicleOwnerId) return;
        const agent = (record.booked_agent_commission_json?.agents || [])
          .find((item) => item.agent_id === agentId);
        if (agent) {
          data.push(externalCommissionService.buildAgentSettlementRowFromCommission(record, agent));
        }
      });
      return {
        data,
        totals: externalCommissionService.sumBulkSettlementTotals(data),
      };
    }
  },

  bulkSettlementPayment: (data) => axios.post(`${API_BASE}/bulk-settlement-payment`, data),

  getVehicleOwners: () => axios.get('/vehicle-owners', { params: { all: true } }),
};

export default externalCommissionService;
